import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { IContactGroup, IContactGroupAddress } from 'contacts/types';
import { Button } from 'core/components';
import { AccessPermissions } from 'core/enums';
import { useAgGridTableContext } from 'core/states';
import { Color, Variant } from 'theme/enums';

import { MergeContactGroups } from './MergeContactGroups';

interface IProps {
  getContactGroups?: (
    selectedRows: IContactGroup[] | IContactGroupAddress[]
  ) => Partial<IContactGroup>[];
}

export const MergeContactGroupsButton = ({ getContactGroups }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const { deselectAllRows, selectedRows, gridApi } = useAgGridTableContext();
  const { hasPermission } = useUserPermissions();

  const selectedContactGroups = useMemo(() => {
    if (getContactGroups) {
      return getContactGroups(selectedRows || []);
    }

    return selectedRows;
  }, [getContactGroups, selectedRows]);

  const isDisabled = selectedContactGroups.length < 2;

  const onSubmitSuccess = () => {
    deselectAllRows();

    gridApi?.refreshServerSide();
  };

  if (!hasPermission(AccessPermissions.ACCESS_CONTACT_GROUP_MERGE)) {
    return <></>;
  }

  return (
    <>
      <Button
        text={t('buttonLabelMergeContactGroups')}
        color={Color.NEUTRAL}
        variant={Variant.OUTLINED}
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
      />

      <MergeContactGroups
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmitSuccess={onSubmitSuccess}
        contactGroups={selectedContactGroups}
      />
    </>
  );
};
